import { Module, Action, Mutation } from "vuex-module-decorators";
import { handlePaginationData, queryRequest } from "@/utils/request";
import { RootState } from "@/store";
import {
  queryGrowthValue,
  queryGrowthValuePage,
  GrowthValueMonth,
  getGrowthValue,
  getGrowthValueCenter
} from "@/graphql/star-level.gql";
import PaginationModule from "@/store/base-modules/PaginationModule";
import { queryDataDictionaryListLang } from "@/graphql/dictionary.gql";
import { formatByDate, getDateBefore36 } from "@/utils/common/date";
import type { PaginationDataState } from "@/types";

export interface GrowthDetailState extends PaginationDataState {
  activeType: number;
  searchCondition: any;
}

@Module({ name: "GrowthDetailModule", namespaced: true })
export default class GrowthDetailModule
  extends PaginationModule<GrowthDetailState, RootState>
  implements GrowthDetailState {
  // 1. 按日，2. 按月
  public activeType: number = 1;
  public dayData = [];
  public monthData = [];

  public listData: any[] = [];
  public searchCondition: any = {}; // 筛选条件
  public openFilterFlag: boolean = false; // 打开筛选模块
  public timeSumMap = {}; // 时间统计集合

  public filterData: any = []; // 筛选条件数据

  public growthValue: number = 0;

  @Mutation
  setGrowthValueMutation(data = 0) {
    this.growthValue = data || 0;
  }
  @Mutation
  setFilterDataMutation(data: any[]) {
    this.filterData = data || [];
  }
  @Mutation
  setActiveTypeMutation(data: number) {
    this.activeType = data || 1;
  }
  @Mutation
  setDayDataMutation(data: any[]) {
    this.dayData = data || [];
  }
  @Mutation
  setMonthDataMutation(data: any[]) {
    this.monthData = data || [];
  }

  @Mutation
  saveListDataMutation(data: any[]) {
    this.listData = data || [];
  }

  @Mutation
  addListDataMutation(data: any[] = []) {
    this.listData.push(...(data || []));
  }

  @Mutation
  setSearchConditionMutation({ key = "", value = "" } = {}) {
    if (!key && !value) {
      this.searchCondition = {};
    } else {
      this.searchCondition[key] = value;
    }
  }
  @Mutation
  setOpenFilterFlagMutation(data: boolean) {
    this.openFilterFlag = data ?? false;
  }
  @Mutation
  setTimeSumMapMutation({ key = "", value = "" } = {}) {
    if (!(key && value)) {
      this.timeSumMap = {};
    } else {
      this.timeSumMap[key] = value;
    }
  }

  @Action
  async queryFilterDataAction() {
    const data = await queryRequest({
      query: queryDataDictionaryListLang,
      variables: {
        input: {
          ddCode: "TBR_GROWTH_SOURCE", // 星级成长值筛选
          lang: window.i18n.locale
        }
      }
    });
    this.context.commit("setFilterDataMutation", data);
  }

  @Action
  async queryGrowthValue(variables) {
    const data = await queryRequest({
      query: queryGrowthValue,
      variables
    });
    const type = this.context.state.activeType;
    const mutationName =
      type === 1 ? "setDayDataMutation" : "setMonthDataMutation";
    this.context.commit(mutationName, data);
  }

  @Action
  async queryListAction({ callback }) {
    const {
      state: {
        searchCondition,
        paginationData,
        paginationData: { pageNum, pageSize }
      },
      commit
    } = this.context;
    const [start, end] = getDateBefore36();
    let { data = [], totalCount } = handlePaginationData(
      await queryRequest({
        query: queryGrowthValuePage,
        variables: {
          input: {
            startDate: searchCondition.startTime || formatByDate(start),
            endDate: searchCondition.endTime || formatByDate(end),
            type: searchCondition._type
          },
          pageInput: { pageNum, pageSize }
        }
      })
    );
    data = await callback(data);
    commit(
      pageNum === 1 ? "saveListDataMutation" : "addListDataMutation",
      data
    );
    // 保存总数
    commit("savePaginationData", { ...paginationData, totalCount });
  }

  @Action
  async GrowthValueMonthAction(variables: any) {
    const data = await queryRequest({
      query: GrowthValueMonth,
      variables
    });
    data.forEach(o => {
      this.context.commit("setTimeSumMapMutation", {
        key: o.year + String(o.month).padStart(2, "0"),
        value: o
      });
    });
  }

  // 总的成长值
  @Action
  async getGrowthValueAction(variables: any) {
    const data = await queryRequest({
      query: getGrowthValueCenter,
      variables
    });
    this.context.commit("setGrowthValueMutation", Number(data.amount) || 0);
  }
}
